import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ImageUploader from '../../components/admin/ImageUploader';

interface PartnerData {
  id: string;
  name: string;
  imageUrl: string;
}

const initialPartners: PartnerData[] = [
  { id: '1', name: 'Garanti BBVA', imageUrl: '' },
  { id: '2', name: 'QNB Finansbank', imageUrl: '' },
  { id: '3', name: 'Vitra', imageUrl: '' },
  { id: '4', name: 'Knauf', imageUrl: '' },
  { id: '5', name: 'İş Bankası', imageUrl: '' },
  { id: '6', name: 'OYAK Çimento', imageUrl: '' },
  { id: '7', name: 'Çuhadaroğlu Alüminyum', imageUrl: '' },
  { id: '8', name: 'Ziraat Bankası', imageUrl: '' },
  { id: '9', name: 'Filli Boya', imageUrl: '' },
  { id: '10', name: 'Kale Seramik', imageUrl: '' },
  { id: '11', name: 'Jotun Boya', imageUrl: '' },
];

export default function PartnersManager() {
  const [partners, setPartners] = useState<PartnerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const docRef = doc(db, 'pages', 'partners');
        const snap = await getDoc(docRef);
        if (snap.exists() && snap.data().items && snap.data().items.length > 0) {
          setPartners(snap.data().items);
        } else {
          setPartners(initialPartners);
          await setDoc(docRef, { items: initialPartners }, { merge: true });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  const handleChange = (id: string, field: string, value: string) => {
    setPartners(partners.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const addPartner = () => {
    const newPartner: PartnerData = {
      id: Date.now().toString(),
      name: 'Yeni Marka',
      imageUrl: ''
    };
    setPartners([...partners, newPartner]);
  };

  const removePartner = (id: string) => {
    if (window.confirm("Bu çözüm ortağını silmek istiyor musunuz?")) {
      setPartners(partners.filter(p => p.id !== id));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await setDoc(doc(db, 'pages', 'partners'), { items: partners }, { merge: true });
      setMessage('Çözüm Ortakları başarıyla güncellendi!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Kayıt başarısız oldu.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500">Yükleniyor...</div>;

  return (
        <div className="space-y-6 pb-24">
      <div className="flex justify-between items-center mb-6">
        <div>
           <h2 className="text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: 'Raleway, sans-serif' }}>Çözüm Ortakları</h2>
           <p className="text-zinc-400 text-sm mt-1">Ana sayfadaki kayan banttaki logoları (Partnerler) yönetin.</p>
        </div>
        <button onClick={addPartner} className="bg-red-600/10 hover:bg-red-600/20 text-red-500 font-bold py-2 px-4 rounded-xl border border-red-600/20 transition-all flex items-center gap-2">
          <i className="ri-add-line"></i> Yeni Ekle
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-xl font-bold text-sm ${message.includes('başarısız') ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'}`}>
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {partners.map((partner, index) => (
          <motion.div key={partner.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 relative">
            <button onClick={() => removePartner(partner.id)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-colors">
              <i className="ri-delete-bin-line"></i>
            </button>
            
            <div className="mb-4 pr-10">
              <label className="block text-zinc-400 text-xs font-bold mb-2 uppercase tracking-wider">Marka Adı</label>
              <input type="text" value={partner.name} onChange={(e) => handleChange(partner.id, 'name', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none" />
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-800">
               <ImageUploader 
                 currentImageUrl={partner.imageUrl} 
                 onImageUploaded={(url) => handleChange(partner.id, 'imageUrl', url)} 
                 onImageDeleted={() => handleChange(partner.id, 'imageUrl', '')} 
                 folderPath={`pages/partners/${partner.id}`}
                 label="Marka Logosu (Şeffaf PNG önerilir)"
               />
            </div>
          </motion.div>
        ))}
      </div>

      <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-zinc-950/80 backdrop-blur-lg border-t border-zinc-800 p-4 flex justify-end z-50">
         <button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white font-black text-lg py-4 px-12 rounded-xl shadow-2xl shadow-red-600/30 transition-all flex items-center gap-3 disabled:opacity-50">
           {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>}
           KAYDET
         </button>
      </div>
    </div>
  );
}
